
































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  extCountdownPresetDigitFontFamily,
  extCountdownPresetDigitFontSize,
  extCountdownPresetSeparatorStyle,
  extCountdownPresetSuffixFontSize,
  extCountdownPresetSuffixTextColor
} from '@/components/wheel/extendedCountdown/extendedCountdownPresets';
import {
  extendedCountdownHorizontalPadding,
  extendedCountdownVerticalMargin,
  extendedCountdownVerticalPadding
} from '@/components/wheel/extendedCountdown/extendedCountdownModel';

export interface CountdownConfig {
  rules: {
    type: 'fixed_duration' | 'fixed_date';
    duration?: { days?: number; hours?: number; minutes?: number; seconds?: number };
    endDate?: string;
    hideOnExpire?: boolean;
  };
  styles: {
    showTimeUnitLabels?: boolean;
    variant?: string | number;
    fontSize?: number;
    textColor?: string;
    fontFamily?: string;
    suffixFontSize?: number;
    suffixFontFamily?: string;
    suffixTextColor?: string;
    separator?: { char?: string; color?: string; fontSize?: number; bold?: boolean };
    digitBackground?: { enabled?: boolean; color?: string; borderRadius?: number };
    spacing?: any;
    padding?: any;
    alignment?: string;
    componentBackground?: string;
    displayParts?: { showHMS?: boolean; showSeconds?: boolean };
  };
}

@Component
export default class ExtendedCountdown extends Vue {
  @Prop({ required: true }) countdown!: CountdownConfig;
  /** B 端预览为 true：展示静态快照，不随真实时间流逝 */
  @Prop({ default: true }) preview!: boolean;

  /** 预览模式下冻结的剩余秒数 */
  private frozenTotalSec: number = 0;
  private previewRulesKey: string = '';

  created() {
    this.syncPreviewSnapshot(true);
  }

  @Watch('countdown.rules.type')
  @Watch('countdown.rules.endDate')
  @Watch('countdown.rules.duration', { deep: true })
  onPreviewRulesChange() {
    this.syncPreviewSnapshot(false);
  }

  buildPreviewRulesKey(): string {
    const r = this.countdown?.rules;
    if (!r) return '';
    const d = r.duration || {};
    return [
      r.type,
      r.endDate || '',
      d.days || 0,
      d.hours || 0,
      d.minutes || 0,
      d.seconds || 0
    ].join('|');
  }

  syncPreviewSnapshot(force: boolean) {
    const key = this.buildPreviewRulesKey();
    if (!force && key === this.previewRulesKey) return;
    this.previewRulesKey = key;
    this.frozenTotalSec = this.computeTotalSeconds(Date.now());
  }

  computeTotalSeconds(nowMs: number): number {
    const r = this.countdown.rules;
    if (r.type === 'fixed_date' && r.endDate) {
      const t = this.parseEndDateTimestamp(r.endDate);
      if (!Number.isNaN(t)) return Math.max(0, Math.floor((t - nowMs) / 1000));
    }
    const d = r.duration || {};
    const sec =
      (d.days || 0) * 86400 +
      (d.hours || 0) * 3600 +
      (d.minutes || 0) * 60 +
      (d.seconds || 0);
    return sec > 0 ? sec : 0;
  }

  parseEndDateTimestamp(endDate?: string): number {
    const s = String(endDate || '').trim();
    const asNum = Number(s);
    if (!Number.isNaN(asNum) && asNum > 1e11) return asNum;
    return Date.parse(s.replace(/-/g, '/'));
  }

  get variant(): 1 | 2 | 3 | 4 {
    const raw = this.countdown?.styles?.variant;
    const str = String(raw == null || raw === '' ? '1' : raw);
    const m = str.match(/[1-4]/);
    const n = m ? parseInt(m[0], 10) : 1;
    return (n >= 1 && n <= 4 ? n : 1) as 1 | 2 | 3 | 4;
  }

  get totalSeconds(): number {
    if (this.preview) return this.frozenTotalSec;
    return this.computeTotalSeconds(Date.now());
  }

  get useLegacyLayout(): boolean {
    if (this.showUnitLabels) return false;
    const r = this.countdown.rules;
    return r.type === 'fixed_date' && this.totalSeconds > 0 && this.totalSeconds < 86400;
  }

  get legacyTripleValues(): string[] {
    const ts = this.totalSeconds;
    const pad2 = (n: number) => (n < 10 ? '0' : '') + n;
    const dp = this.countdown.styles.displayParts || {};
    const showHMS = dp.showHMS !== false;
    const showSeconds = dp.showSeconds !== false;
    const r = this.countdown.rules;

    // 固定日期不足 1 小时或已过期：仅展示 分:秒（不含毫秒、不含 HH）
    if (r.type === 'fixed_date' && ts < 3600) {
      const mm = pad2(Math.floor(ts / 60));
      const ss = pad2(ts % 60);
      if (!showHMS) return ['00', '00'];
      if (!showSeconds) return [mm];
      return [mm, ss];
    }

    const hh = pad2(Math.floor((ts % 86400) / 3600));
    const mm = pad2(Math.floor((ts % 3600) / 60));
    const ss = pad2(ts % 60);
    if (!showHMS) return ['00', '00', '00'];
    if (!showSeconds) return [hh, mm, '00'];
    return [hh, mm, ss];
  }

  get displayParts(): { value: string; unit: string }[] {
    const r = this.countdown.rules;
    const st = this.countdown.styles;
    const dp = st.displayParts || {};
    const ts = this.totalSeconds;
    const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

    if (r.type === 'fixed_duration') {
      const mm = pad2(Math.floor(ts / 60));
      const ss = pad2(ts % 60);
      const showHMS = dp.showHMS !== false;
      const showSeconds = dp.showSeconds !== false;
      const out: any[] = [];
      if (showHMS) {
        out.push({ value: mm, unit: 'MM' });
        out.push({ value: ss, unit: 'SS' });
        if (showSeconds) out.push({ value: '00', unit: 'MS' });
      }
      return out.length ? out : [{ value: mm, unit: 'MM' }, { value: ss, unit: 'SS' }];
    }

    // 固定日期不足 1 小时或已过期：仅展示 MM / SS（不含 HH、不含 MS）
    if (r.type === 'fixed_date' && ts < 3600) {
      const mm = pad2(Math.floor(ts / 60));
      const ss = pad2(ts % 60);
      const showHMS = dp.showHMS !== false;
      const showSeconds = dp.showSeconds !== false;
      const out: any[] = [];
      if (showHMS) {
        out.push({ value: mm, unit: 'MM' });
        if (showSeconds) out.push({ value: ss, unit: 'SS' });
      }
      return out.length ? out : [{ value: mm, unit: 'MM' }, { value: ss, unit: 'SS' }];
    }

    const days = Math.floor(ts / 86400);
    const hh = pad2(Math.floor((ts % 86400) / 3600));
    const mm = pad2(Math.floor((ts % 3600) / 60));
    const ss = pad2(ts % 60);
    const showDays = dp.showDays !== false;
    const showHMS = dp.showHMS !== false;
    const showSeconds = dp.showSeconds !== false;
    const hideDaysBecauseUnderOneDay = r.type === 'fixed_date' && ts > 0 && ts < 86400;
    const out: any[] = [];
    if (!hideDaysBecauseUnderOneDay && (showDays || days > 0)) {
      out.push({ value: pad2(days), unit: 'DD' });
    }
    if (showHMS) {
      out.push({ value: hh, unit: 'HH' });
      out.push({ value: mm, unit: 'MM' });
      if (showSeconds) out.push({ value: ss, unit: 'SS' });
    }
    return out.length ? out : [{ value: '00', unit: 'SS' }];
  }

  get showUnitLabels(): boolean {
    return this.countdown.styles?.showTimeUnitLabels === true;
  }

  get showBetweenSeparators(): boolean {
    return this.variant === 2 || this.variant === 4;
  }

  get sepChar(): string {
    const ch = this.countdown.styles?.separator?.char;
    return ch != null && String(ch) !== '' ? String(ch) : ':';
  }

  get rowStyle() {
    const s = this.countdown.styles || {};
    const sp = s.spacing || {};
    const pad = s.padding || {};
    const a = s.alignment || 'center';
    const jc = a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : 'center';
    const horiz = extendedCountdownHorizontalPadding(pad, sp);
    const vertMargin = extendedCountdownVerticalMargin(sp);
    const vertPad = extendedCountdownVerticalPadding(s.componentBackground, pad);
    return {
      display: 'flex',
      flexDirection: 'row' as const,
      justifyContent: jc,
      alignItems: 'center',
      fontSize: 'initial',
      fontWeight: 'normal',
      boxSizing: 'border-box',
      width: horiz.width ?? '100%',
      maxWidth: '100%',
      minWidth: 0,
      overflowX: 'hidden',
      marginTop: vertMargin.marginTop,
      marginBottom: vertMargin.marginBottom,
      marginLeft: horiz.marginLeft,
      marginRight: horiz.marginRight,
      paddingTop: vertPad.paddingTop,
      paddingBottom: vertPad.paddingBottom,
      paddingLeft: horiz.paddingLeft,
      paddingRight: horiz.paddingRight,
      backgroundColor: s.componentBackground || undefined
    };
  }

  get digitBoxStyle() {
    const db = this.countdown.styles.digitBackground || {};
    const br = db.borderRadius != null ? `${db.borderRadius}px` : '4px';
    const enabled = db.enabled !== false;
    const fontFamily = this.resolvedDigitFontFamily();
    return {
      background: enabled ? db.color || '#333333' : 'transparent',
      borderRadius: br,
      ...(fontFamily ? { fontFamily } : {})
    };
  }

  /** 配置区写入的 countdown.styles.fontFamily（非空才参与预览） */
  resolvedDigitFontFamily(): string | undefined {
    const raw = this.countdown.styles?.fontFamily;
    const v = raw != null ? String(raw).trim() : '';
    if (v) return v;
    const fallback = extCountdownPresetDigitFontFamily(String(this.countdown.styles?.variant || '1'));
    return fallback || undefined;
  }

  presetDigitFontSize(): number {
    const v = String(this.countdown.styles?.variant || '1');
    const s = this.countdown.styles || {};
    return s.fontSize != null ? Number(s.fontSize) : extCountdownPresetDigitFontSize(v);
  }

  get digitTextStyle() {
    const s = this.countdown.styles || {};
    const fontFamily = this.resolvedDigitFontFamily();
    return {
      color: s.textColor || '#ffffff',
      fontSize: `${this.presetDigitFontSize()}px`,
      ...(fontFamily ? { fontFamily } : {}),
      fontWeight: 700
    };
  }

  get sepStyle() {
    const s = this.countdown.styles || {};
    const digitFs = `${this.presetDigitFontSize()}px`;
    const presetSep = extCountdownPresetSeparatorStyle(String(s.variant || '1'));
    const sepColorRaw = s.separator?.color;
    const color =
      sepColorRaw != null && String(sepColorRaw).trim() !== ''
        ? String(sepColorRaw).trim()
        : presetSep.color || '#000000';
    const fontSize =
      s.separator?.fontSize != null
        ? `${s.separator.fontSize}px`
        : presetSep.fontSize != null
          ? `${presetSep.fontSize}px`
          : digitFs;
    const fontWeight =
      s.separator?.bold != null ? (s.separator.bold ? 'bold' : undefined) : presetSep.bold ? 'bold' : undefined;
    const fontFamily = this.resolvedDigitFontFamily();
    return { color, fontSize, fontWeight, ...(fontFamily ? { fontFamily } : {}) };
  }

  /** 预设 4：分隔符与数字 baseline（底部）对齐，不参与底标行高 */
  get sepRowAlignStyle(): Record<string, string | number> {
    if (this.variant !== 4) return {};
    return {
      alignSelf: 'auto',
      lineHeight: 1,
      flexShrink: 0
    };
  }

  get mergedSepStyle() {
    return { ...this.sepStyle, ...this.sepRowAlignStyle };
  }

  get flipOuterStyle() {
    return {
      ...this.digitBoxStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative' as const,
      overflow: 'hidden' as const,
      padding: '8px 10px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
      '--flip-line-color': this.flipLineColor || 'rgba(255,255,255,0.2)'
    };
  }

  get flipLineColor() {
    return this.countdown.styles?.digitBackground?.flipLineColor || '';
  }

  get minimalTextStyle() {
    const s = this.countdown.styles || {};
    const fontFamily = this.resolvedDigitFontFamily();
    const fs = this.presetDigitFontSize();
    const base = {
      color: s.textColor || '#1D2129',
      fontSize: `${fs}px`,
      ...(fontFamily ? { fontFamily } : {}),
      fontWeight: 700,
      letterSpacing: '0.02em'
    };
    // 预设 4：自然行高 + baseline 对齐（与分隔符底部对齐）
    return {
      ...base,
      display: 'inline-block',
      lineHeight: 1
    };
  }

  get unitLabelStackedStyle() {
    const s = this.countdown.styles || {};
    const variant = String(s.variant || '1');
    const suffixColorRaw = s.suffixTextColor;
    const labelColor =
      suffixColorRaw != null && String(suffixColorRaw).trim() !== ''
        ? String(suffixColorRaw).trim()
        : extCountdownPresetSuffixTextColor(variant);
    const suffixFs =
      s.suffixFontSize != null ? Number(s.suffixFontSize) : extCountdownPresetSuffixFontSize(variant);
    const suffixRaw = s.suffixFontFamily;
    const suffixFont = suffixRaw != null ? String(suffixRaw).trim() : '';
    const digitFont = this.resolvedDigitFontFamily();
    const fontFamily = suffixFont || digitFont;
    // 预设 4：仅用父级 .cartx-b-time-ext-segment { gap: 2px }，勿叠加 marginTop
    const marginTop = variant === '4' ? '0px' : '4px';
    return {
      color: labelColor,
      fontSize: `${suffixFs}px`,
      lineHeight: 1.2,
      ...(fontFamily ? { fontFamily } : {}),
      fontWeight: 400,
      marginTop
    };
  }

  splitChars(segment: string): string[] {
    const t = String(segment == null ? '00' : segment);
    return t.length ? t.split('') : ['0', '0'];
  }
}
